/**
 * Custom hook for managing first-time tooltips on expansion icons.
 * Randomly selects one undismissed tooltip to show on each page load.
 * Dismissal is permanent and stored in localStorage.
 */

import { useState, useEffect, useCallback } from 'react';

// Storage key prefix for tooltip dismissal (localStorage - permanent)
const TOOLTIP_STORAGE_PREFIX = 'aiprep_tooltip_dismissed_';

// Define the tooltip IDs
export type TooltipId =
  | 'problem-description'
  | 'detailed-algorithm'
  | 'solution-code'
  | 'expand-all'
  | 'practice-workbench';

// Contextual tooltip IDs (not part of random rotation)
export type ContextualTooltipId = 'animation-button';

// Tooltip content for each icon
export const TOOLTIP_CONTENT: Record<TooltipId, string> = {
  'problem-description': 'Click to expand problem details',
  'detailed-algorithm': 'Click to see the algorithm explanation',
  'solution-code': 'Click to view the solution code',
  'expand-all': 'Click to expand/collapse all sections',
  'practice-workbench': 'Click to open the practice area',
};

// Content for contextual tooltips
export const CONTEXTUAL_TOOLTIP_CONTENT: Record<ContextualTooltipId, string> = {
  'animation-button': 'Generate visual animation of the algorithm',
};

// All tooltip IDs
const ALL_TOOLTIPS: TooltipId[] = [
  'problem-description',
  'detailed-algorithm',
  'solution-code',
  'expand-all',
  'practice-workbench',
];

// Module-level cache for the selected tooltip.
// This resets on page refresh (new JS context) but persists during SPA navigation.
let cachedSelection: TooltipId | null | undefined = undefined;

/**
 * Check if a tooltip has been dismissed (permanently)
 */
const isTooltipDismissed = (id: TooltipId): boolean => {
  const dismissed = localStorage.getItem(`${TOOLTIP_STORAGE_PREFIX}${id}`);
  return dismissed === 'true';
};

/**
 * Mark a tooltip as dismissed (permanently)
 */
const dismissTooltip = (id: TooltipId): void => {
  localStorage.setItem(`${TOOLTIP_STORAGE_PREFIX}${id}`, 'true');
};

/**
 * Check if a contextual tooltip has been dismissed (permanently)
 */
const isContextualTooltipDismissed = (id: ContextualTooltipId): boolean => {
  const dismissed = localStorage.getItem(`${TOOLTIP_STORAGE_PREFIX}${id}`);
  return dismissed === 'true';
};

/**
 * Mark a contextual tooltip as dismissed (permanently)
 */
const dismissContextualTooltip = (id: ContextualTooltipId): void => {
  localStorage.setItem(`${TOOLTIP_STORAGE_PREFIX}${id}`, 'true');
};

/**
 * Get all undismissed tooltip IDs
 */
const getUndismissedTooltips = (): TooltipId[] => {
  return ALL_TOOLTIPS.filter(id => !isTooltipDismissed(id));
};

/**
 * Clear the cached selection (called when tooltip is dismissed)
 */
const clearCachedSelection = (): void => {
  cachedSelection = undefined;
};

/**
 * Get the current cached selection (for testing)
 */
const getCachedSelection = (): TooltipId | null | undefined => {
  return cachedSelection;
};

/**
 * Set the cached selection (for testing)
 */
const setCachedSelection = (id: TooltipId | null | undefined): void => {
  cachedSelection = id;
};

/**
 * Randomly select a tooltip from undismissed ones.
 * Uses module-level cache to ensure the same tooltip is shown for the entire page load.
 * Resets on page refresh (new JS execution context).
 */
const selectRandomTooltip = (): TooltipId | null => {
  // Check if we already selected a tooltip for this page load
  if (cachedSelection !== undefined) {
    // Verify it's still undismissed
    if (cachedSelection === null || !isTooltipDismissed(cachedSelection)) {
      return cachedSelection;
    }
    // It was dismissed, need to pick again
    cachedSelection = undefined;
  }

  // Get all undismissed tooltips
  const undismissed = getUndismissedTooltips();

  if (undismissed.length === 0) {
    cachedSelection = null;
    return null;
  }

  // Randomly pick one
  const randomIndex = Math.floor(Math.random() * undismissed.length);
  const selected = undismissed[randomIndex];

  // Cache for this page load
  cachedSelection = selected;

  return selected;
};

interface UseFirstTimeTooltipResult {
  /** The currently active tooltip ID, or null if none should be shown */
  activeTooltip: TooltipId | null;
  /** Check if a specific tooltip should be shown */
  shouldShowTooltip: (id: TooltipId) => boolean;
  /** Dismiss a tooltip (called when user clicks the icon) */
  handleTooltipDismiss: (id: TooltipId) => void;
  /** Get the tooltip content for a given ID */
  getTooltipContent: (id: TooltipId) => string;
  /** Check if all tooltips have been dismissed */
  allTooltipsDismissed: boolean;
  /** Check if a contextual tooltip should be shown (not part of random rotation) */
  shouldShowContextualTooltip: (id: ContextualTooltipId) => boolean;
  /** Dismiss a contextual tooltip */
  handleContextualTooltipDismiss: (id: ContextualTooltipId) => void;
  /** Get content for a contextual tooltip */
  getContextualTooltipContent: (id: ContextualTooltipId) => string;
}

export const useFirstTimeTooltip = (): UseFirstTimeTooltipResult => {
  const [activeTooltip, setActiveTooltip] = useState<TooltipId | null>(null);
  const [dismissedTooltips, setDismissedTooltips] = useState<Set<TooltipId>>(new Set());

  // Initialize on mount - check localStorage for dismissed tooltips and randomly select one
  useEffect(() => {
    const dismissed = new Set<TooltipId>();
    for (const id of ALL_TOOLTIPS) {
      if (isTooltipDismissed(id)) {
        dismissed.add(id);
      }
    }
    setDismissedTooltips(dismissed);

    // Randomly select a tooltip (cached for this page load)
    const selected = selectRandomTooltip();
    setActiveTooltip(selected);
  }, []);

  const shouldShowTooltip = useCallback((id: TooltipId): boolean => {
    return activeTooltip === id && !dismissedTooltips.has(id);
  }, [activeTooltip, dismissedTooltips]);

  const handleTooltipDismiss = useCallback((id: TooltipId) => {
    // Mark as dismissed in localStorage (permanent)
    dismissTooltip(id);

    // Clear the cached selection
    clearCachedSelection();

    // Update local state
    setDismissedTooltips(prev => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });

    // Hide the current tooltip
    setActiveTooltip(null);
  }, []);

  const getTooltipContent = useCallback((id: TooltipId): string => {
    return TOOLTIP_CONTENT[id];
  }, []);

  const allTooltipsDismissed = dismissedTooltips.size === ALL_TOOLTIPS.length;

  // Contextual tooltip handlers (separate from random rotation)
  const [dismissedContextualTooltips, setDismissedContextualTooltips] = useState<Set<ContextualTooltipId>>(new Set());

  // Initialize contextual tooltips dismissal state
  useEffect(() => {
    const dismissed = new Set<ContextualTooltipId>();
    if (isContextualTooltipDismissed('animation-button')) {
      dismissed.add('animation-button');
    }
    setDismissedContextualTooltips(dismissed);
  }, []);

  const shouldShowContextualTooltip = useCallback((id: ContextualTooltipId): boolean => {
    return !dismissedContextualTooltips.has(id);
  }, [dismissedContextualTooltips]);

  const handleContextualTooltipDismiss = useCallback((id: ContextualTooltipId) => {
    dismissContextualTooltip(id);
    setDismissedContextualTooltips(prev => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  }, []);

  const getContextualTooltipContent = useCallback((id: ContextualTooltipId): string => {
    return CONTEXTUAL_TOOLTIP_CONTENT[id];
  }, []);

  return {
    activeTooltip,
    shouldShowTooltip,
    handleTooltipDismiss,
    getTooltipContent,
    allTooltipsDismissed,
    shouldShowContextualTooltip,
    handleContextualTooltipDismiss,
    getContextualTooltipContent,
  };
};

// Export utility functions for testing
export {
  isTooltipDismissed,
  dismissTooltip,
  getUndismissedTooltips,
  selectRandomTooltip,
  clearCachedSelection,
  getCachedSelection,
  setCachedSelection,
  ALL_TOOLTIPS,
  isContextualTooltipDismissed,
  dismissContextualTooltip,
};
